// Get backend URL based on environment
const getBackendUrl = () => {
  if (import.meta.env.MODE === 'development') {
    return 'http://localhost:5001';
  }
  return ''; // Use relative path in production (proxied by nginx)
};

const API_BASE = `${getBackendUrl()}/api/v1`;

// Get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('tkn');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

// Get appointments
export const getAppointments = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_BASE}/appointments?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch appointments');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

// Get today's appointments
export const getTodayAppointments = async (providerId) => {
  try {
    const params = providerId ? `?providerId=${providerId}` : '';
    const response = await fetch(`${API_BASE}/appointments/today${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch today appointments');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching today appointments:', error);
    throw error;
  }
};

// Create appointment
export const createAppointment = async (appointmentData) => {
  try {
    const response = await fetch(`${API_BASE}/appointments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(appointmentData),
    });

    if (!response.ok) {
      throw new Error('Failed to create appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
};

// Update appointment
export const updateAppointment = async (id, appointmentData) => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(appointmentData),
    });

    if (!response.ok) {
      throw new Error('Failed to update appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

// Confirm appointment
export const confirmAppointment = async (id) => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${id}/confirm`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to confirm appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error confirming appointment:', error);
    throw error;
  }
};

// Complete appointment
export const completeAppointment = async (id, visitData) => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${id}/complete`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(visitData),
    });

    if (!response.ok) {
      throw new Error('Failed to complete appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error completing appointment:', error);
    throw error;
  }
};

// Cancel appointment
export const cancelAppointment = async (id, reason, cancelledBy = 'Staff') => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${id}/cancel`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason, cancelledBy }),
    });

    if (!response.ok) {
      throw new Error('Failed to cancel appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
};

// Mark no-show
export const markNoShow = async (id, reason) => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${id}/no-show`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error('Failed to mark no-show');
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking no-show:', error);
    throw error;
  }
};

// Get appointment statistics
export const getAppointmentStats = async () => {
  try {
    const response = await fetch(`${API_BASE}/appointments/stats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch appointment statistics');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching appointment stats:', error);
    throw error;
  }
};

// Delete appointment
export const deleteAppointment = async (appointmentId) => {
  try {
    const response = await fetch(`${API_BASE}/appointments/${appointmentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete appointment');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};
